import { useCallback, useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeTags } from "@/tags/values"
import {
  transport,
  useTransportCollectionQuery,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import type { ListPersonasRequest } from "@/types_api/backend/transport/list_personas_request"
import type { PersonaResource } from "@/types_api/backend/transport/list_personas_response"
import type { Persona } from "@/types/api/Persona"

export const PERSONAS_LIST_TRANSPORT_REQUEST = {
  page_size: 1000,
} satisfies ListPersonasRequest

type PersonasListData = Persona[]

interface LookupHookOptions {
  enabled?: boolean
}

interface UsePersonasListOptions<T> {
  enabled?: boolean
  select?: (data: PersonasListData) => T
  tagFilters?: string[]
}

interface TransportLoadingStateInput {
  data: unknown
  enabled: boolean
  error: unknown
  isLoading: boolean
}

function isPersonaTransportQueryLoading({
  data,
  enabled,
  error,
  isLoading,
}: TransportLoadingStateInput): boolean {
  return isLoading || (enabled && data == null && error == null)
}

export function mapTransportPersonaToPersona(
  persona: PersonaResource,
): Persona {
  return {
    attribution: persona.attribution,
    id: persona.id,
    organization_id: persona.organization_id ?? null,
    display_name: persona.display_name,
    metadata: (persona.metadata ?? {}) as Persona["metadata"],
    simulated_user_prompt: persona.simulated_user_prompt ?? "",
    avatar_url: persona.avatar_url ?? "",
    voice_name: persona.voice_name ?? null,
    language_code: persona.language_code ?? null,
    created_at: persona.created_at,
    last_updated_at: persona.last_updated_at,
    created_by_user_id: persona.created_by_user_id ?? null,
    active: true,
    tags: persona.tags ?? [],
  }
}

export function usePersonasList<T = PersonasListData>(
  options?: UsePersonasListOptions<T>,
) {
  const { enabled, select, tagFilters } = options ?? {}
  const queryEnabled = enabled ?? true
  const normalizedTagFilters = useMemo(
    () => normalizeTags(tagFilters ?? []),
    [tagFilters],
  )
  const request = useMemo<ListPersonasRequest>(() => {
    if (normalizedTagFilters.length === 0) {
      return PERSONAS_LIST_TRANSPORT_REQUEST
    }

    return {
      ...PERSONAS_LIST_TRANSPORT_REQUEST,
      tag_filters: normalizedTagFilters as ListPersonasRequest["tag_filters"],
    }
  }, [normalizedTagFilters])

  const query = useTransportCollectionQuery<"listPersonas", PersonaResource>({
    operation: transport.collections.listPersonas,
    request,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
    },
  })
  const personas = useMemo(
    () => query.data?.items.map(mapTransportPersonaToPersona),
    [query.data?.items],
  )
  const selectedData = useMemo(
    () => (personas && select ? select(personas) : personas),
    [personas, select],
  )

  return {
    ...query,
    data: selectedData,
    isLoading: isPersonaTransportQueryLoading({
      data: query.data,
      enabled: queryEnabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function usePersonaById(
  personaId: string | null | undefined,
  options?: LookupHookOptions,
) {
  const enabled = Boolean(personaId) && (options?.enabled ?? true)
  const query = useTransportQuery({
    operation: transport.operations.getPersona,
    request: {
      persona_id: personaId ?? "",
    },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
    },
  })
  const persona = useMemo(
    () =>
      query.data ? mapTransportPersonaToPersona(query.data.persona) : null,
    [query.data],
  )

  return {
    ...query,
    data: persona,
    isLoading: isPersonaTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function usePersonasById(options?: LookupHookOptions) {
  const { data, isLoading, isError, error } = usePersonasList({
    enabled: options?.enabled,
  })

  const personasById = useMemo(() => {
    return (data || []).reduce<Record<string, Persona>>((acc, persona) => {
      if (persona) {
        acc[persona.id] = persona
      }
      return acc
    }, {})
  }, [data])

  return {
    personasById,
    isLoading,
    isError,
    error,
  }
}

/**
 * Revalidates the persona catalog for a change this tab's query cache cannot
 * see: a creation form opened in another tab invalidates that tab's cache, not
 * this one, and the catalog refetches on neither mount nor focus.
 */
export function useRevalidatePersonasCatalog() {
  const transportQueryUtils = useTransportQueryUtils()

  return useCallback(
    () => void transportQueryUtils.invalidateOperation("listPersonas"),
    [transportQueryUtils],
  )
}
